/**
 * Composant d'auto-complétion pour inviter un joueur dans une table brouillon.
 *
 * UX :
 * - Au focus : affiche les joueurs connectés (disponibles par défaut)
 * - En tapant (2+ chars) : recherche dans la base joueurs via /api/players/search
 * - Les résultats sont croisés avec la liste des connectés/en jeu pour le badge statut
 * - Les filtres "Connectés" / "Disponibles" sont activés par défaut mais désactivables
 * - Profils on_fire / bougon visibles côté inviteur
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import { BACKEND_URL } from '../config';
import { isBotPseudo } from '../utils/pseudo';
import type { AvailabilityProfile, CompetRolePreference } from '../types/draft';
import { AVAILABILITY_LABELS, COMPET_ROLE_LABELS } from '../types/draft';

interface ConnectedUserEntry {
  pseudo: string;
  pseudoDisplay: string;
  availability?: AvailabilityProfile;
  competRolePreference?: CompetRolePreference;
}

interface DbPlayerEntry {
  pseudo: string;
  pseudoDisplay: string;
  hasPhoto: boolean;
  photoUrl?: string | null;
  team?: { id: number; nom: string } | null;
}

type PlayerStatus = 'connected' | 'playing' | 'offline';

interface PlayerRow {
  pseudo: string;
  pseudoDisplay: string;
  photoUrl?: string | null;
  teamName?: string;
  status: PlayerStatus;
  availability: AvailabilityProfile;
  competRolePreference: CompetRolePreference;
}

interface PlayerInviteSearchProps {
  playingUsers: Set<string>;
  draftPseudos: Set<string>;
  currentPseudo: string;
  compet: boolean;
  token: string;
  onInvite: (pseudo: string) => void;
  onClose: () => void;
}

const STATUS_CONFIG: Record<PlayerStatus, { label: string; dotClass: string }> = {
  connected: { label: 'Connecté', dotClass: 'connected' },
  playing:   { label: 'En jeu', dotClass: 'playing' },
  offline:   { label: 'Hors ligne', dotClass: 'offline' },
};

function resolveAvailability(
  pseudo: string,
  connectedMap: Map<string, ConnectedUserEntry>,
  searchAvailability: Map<string, AvailabilityProfile>,
): AvailabilityProfile {
  const connected = connectedMap.get(pseudo.toLowerCase());
  if (connected?.availability) return connected.availability;
  return searchAvailability.get(pseudo) ?? 'detendu';
}

function resolveCompetRolePreference(
  pseudo: string,
  connectedMap: Map<string, ConnectedUserEntry>,
  searchRoles: Map<string, CompetRolePreference>,
): CompetRolePreference {
  const connected = connectedMap.get(pseudo.toLowerCase());
  if (connected?.competRolePreference) return connected.competRolePreference;
  return searchRoles.get(pseudo) ?? 'none';
}

export default function PlayerInviteSearch({
  playingUsers,
  draftPseudos,
  currentPseudo,
  compet,
  token,
  onInvite,
  onClose,
}: PlayerInviteSearchProps) {
  const [query, setQuery] = useState('');
  const [filterConnected, setFilterConnected] = useState(true);
  const [filterAvailable, setFilterAvailable] = useState(true);

  const [connectedMap, setConnectedMap] = useState<Map<string, ConnectedUserEntry>>(new Map());
  const [searchAvailability, setSearchAvailability] = useState<Map<string, AvailabilityProfile>>(new Map());
  const [searchCompetRoles, setSearchCompetRoles] = useState<Map<string, CompetRolePreference>>(new Map());
  const [dbResults, setDbResults] = useState<DbPlayerEntry[]>([]);
  const [loadingConnected, setLoadingConnected] = useState(true);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/connected-users?compet=${compet}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled && json.success) {
          const map = new Map<string, ConnectedUserEntry>();
          for (const u of json.data as ConnectedUserEntry[]) {
            map.set(u.pseudo.toLowerCase(), u);
          }
          setConnectedMap(map);
        }
      } catch (err) {
        console.error('[PlayerInviteSearch] fetch connected-users error:', err);
      } finally {
        if (!cancelled) setLoadingConnected(false);
      }
    })();
    return () => { cancelled = true; };
  }, [compet, token]);

  useEffect(() => {
    if (dbResults.length === 0) {
      setSearchAvailability(new Map());
      setSearchCompetRoles(new Map());
      return;
    }

    let cancelled = false;
    const pseudos = dbResults.map(p => p.pseudo).join(',');
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/profile/availability/map?pseudos=${encodeURIComponent(pseudos)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok || cancelled) return;
        const json = await res.json() as {
          profiles?: Record<string, AvailabilityProfile>;
          competRolePreferences?: Record<string, CompetRolePreference>;
        };
        if (cancelled) return;
        setSearchAvailability(new Map(Object.entries(json.profiles ?? {})));
        setSearchCompetRoles(new Map(Object.entries(json.competRolePreferences ?? {})));
      } catch (err) {
        console.error('[PlayerInviteSearch] fetch availability map error:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [dbResults, token]);

  useEffect(() => {
    if (!loadingConnected) inputRef.current?.focus();
  }, [loadingConnected]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const searchDb = useCallback(async (q: string) => {
    if (q.length < 2) {
      setDbResults([]);
      return;
    }
    setLoadingSearch(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(q)}&limit=15&compet=${compet}`,
      );
      const json = await res.json();
      setDbResults(json.players || []);
    } catch (err) {
      console.error('[PlayerInviteSearch] search error:', err);
      setDbResults([]);
    } finally {
      setLoadingSearch(false);
    }
  }, [compet]);

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchDb(val), 250);
  }, [searchDb]);

  const getStatus = useCallback((pseudo: string): PlayerStatus => {
    if (playingUsers.has(pseudo)) return 'playing';
    if (connectedMap.has(pseudo.toLowerCase())) return 'connected';
    return 'offline';
  }, [playingUsers, connectedMap]);

  const displayRows: PlayerRow[] = useMemo(() => {
    const hasQuery = query.trim().length >= 2;
    let raw: PlayerRow[];

    if (hasQuery && dbResults.length > 0) {
      raw = dbResults.map(p => ({
        pseudo: p.pseudo,
        pseudoDisplay: p.pseudoDisplay,
        photoUrl: p.photoUrl,
        teamName: p.team?.nom,
        status: getStatus(p.pseudo),
        availability: resolveAvailability(p.pseudo, connectedMap, searchAvailability),
        competRolePreference: resolveCompetRolePreference(p.pseudo, connectedMap, searchCompetRoles),
      }));
    } else if (hasQuery) {
      raw = [];
    } else {
      raw = Array.from(connectedMap.values()).map(u => ({
        pseudo: u.pseudo,
        pseudoDisplay: u.pseudoDisplay,
        status: getStatus(u.pseudo),
        availability: u.availability ?? 'detendu',
        competRolePreference: u.competRolePreference ?? 'none',
      }));
    }

    const draftPseudosLower = new Set(Array.from(draftPseudos).map(s => s.toLowerCase()));
    raw = raw.filter(
      p => p.pseudo.toLowerCase() !== currentPseudo.toLowerCase()
        && !isBotPseudo(p.pseudo)
        && !draftPseudosLower.has(p.pseudo.toLowerCase())
    );

    if (filterConnected) {
      raw = raw.filter(p => p.status !== 'offline');
    }
    if (filterAvailable) {
      raw = raw.filter(p => p.status === 'connected');
    }

    const order: Record<PlayerStatus, number> = { connected: 0, playing: 1, offline: 2 };
    raw.sort((a, b) => {
      const aOnFire = a.availability === 'on_fire' ? 0 : 1;
      const bOnFire = b.availability === 'on_fire' ? 0 : 1;
      if (aOnFire !== bOnFire) return aOnFire - bOnFire;
      return order[a.status] - order[b.status];
    });

    return raw;
  }, [query, dbResults, connectedMap, searchAvailability, searchCompetRoles, currentPseudo, draftPseudos, filterConnected, filterAvailable, getStatus]);

  const connectedCount = connectedMap.size;
  const isLoading = loadingConnected || loadingSearch;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  return (
    <div className="player-invite-search" ref={panelRef} onKeyDown={handleKeyDown}>
      <div className="player-invite-header">
        <span className="player-invite-title">Inviter un joueur</span>
        <button className="player-invite-close" onClick={onClose} title="Fermer">✕</button>
      </div>

      <input
        ref={inputRef}
        type="text"
        className="player-invite-input"
        placeholder="Rechercher un joueur..."
        value={query}
        onChange={handleQueryChange}
      />

      <div className="player-invite-filters">
        <span className="player-invite-filter-info">
          <span className="filter-dot connected" />
          {connectedCount} en ligne
        </span>
        <label className={`player-invite-filter-toggle ${filterConnected ? 'active' : ''}`}>
          <input
            type="checkbox"
            checked={filterConnected}
            onChange={e => { setFilterConnected(e.target.checked); if (!e.target.checked) setFilterAvailable(false); }}
          />
          Connectés
        </label>
        <label className={`player-invite-filter-toggle ${filterAvailable ? 'active' : ''}`}>
          <input
            type="checkbox"
            checked={filterAvailable}
            onChange={e => { setFilterAvailable(e.target.checked); if (e.target.checked) setFilterConnected(true); }}
          />
          Disponibles
        </label>
      </div>

      <div className="player-invite-results">
        {isLoading ? (
          <div className="player-invite-empty">
            <div className="loading-spinner-small" />
            Chargement...
          </div>
        ) : displayRows.length > 0 ? (
          displayRows.map(player => {
            const cfg = STATUS_CONFIG[player.status];
            const availabilityInfo = AVAILABILITY_LABELS[player.availability];
            const roleInfo = player.competRolePreference !== 'none'
              ? COMPET_ROLE_LABELS[player.competRolePreference]
              : null;
            const inviteTitle = player.availability === 'bougon'
              ? `${player.pseudoDisplay} est en mode bougon — l'invitation sera probablement refusée`
              : roleInfo
                ? `Inviter ${player.pseudoDisplay} (${roleInfo.label.toLowerCase()})`
                : `Inviter ${player.pseudoDisplay}`;

            return (
              <div key={player.pseudo} className="player-invite-row">
                <PlayerAvatar pseudo={player.pseudo} size={28} photoUrl={player.photoUrl} />
                <div className="player-invite-info">
                  <span className="player-invite-pseudo">
                    {player.pseudoDisplay}
                    {(player.availability === 'on_fire' || player.availability === 'bougon') && (
                      <span
                        className={`player-invite-availability ${player.availability}`}
                        title={availabilityInfo.description}
                      >
                        {availabilityInfo.emoji}
                      </span>
                    )}
                    {compet && roleInfo && (
                      <span
                        className={`player-invite-role player-invite-role--${player.competRolePreference}`}
                        title={roleInfo.description}
                      >
                        {roleInfo.short}
                      </span>
                    )}
                  </span>
                  {player.teamName && (
                    <span className="player-invite-team">{player.teamName}</span>
                  )}
                </div>
                <span className={`player-invite-status-badge status-${player.status}`}>
                  <span className={`status-dot ${cfg.dotClass}`} />
                  {cfg.label}
                  {player.availability === 'on_fire' && player.status === 'connected' && (
                    <span className="player-invite-ready-label">Prêt</span>
                  )}
                </span>
                <button
                  className={`player-invite-btn ${player.status === 'offline' ? 'offline' : ''}`}
                  onClick={() => onInvite(player.pseudo)}
                  title={inviteTitle}
                >
                  Inviter
                </button>
              </div>
            );
          })
        ) : (
          <div className="player-invite-empty">
            {query.trim().length >= 2
              ? 'Aucun joueur trouvé'
              : filterAvailable
                ? 'Aucun joueur disponible'
                : filterConnected
                  ? 'Aucun joueur connecté'
                  : 'Tapez au moins 2 caractères pour rechercher'}
          </div>
        )}
      </div>
    </div>
  );
}
